import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispute } from '../../context/DisputeContext';
import { classifyDispute } from '../../utils/api';
import ProgressBar from '../../components/ProgressBar';

const DISPUTE_TYPES = [
  {
    id: 'late_payment',
    title: 'Late Payment',
    description: 'Unpaid invoices or overdue payments',
    icon: '💰',
    successRate: 81,
    examples: ['Unpaid invoices', 'Overdue bills', 'Payment disputes'],
  },
  {
    id: 'security_deposit',
    title: 'Security Deposit',
    description: 'Rental deposit disputes or deductions',
    icon: '🏠',
    successRate: 75,
    examples: ['Deposit not returned', 'Unfair deductions', 'Damage disputes'],
  },
  {
    id: 'product_dispute',
    title: 'Product Dispute',
    description: 'Issues with products, services, or warranties',
    icon: '🛒',
    successRate: 72,
    examples: ['Defective products', 'Poor service', 'Warranty claims'],
  },
  {
    id: 'personal_loan',
    title: 'Personal Loan',
    description: 'Disputes over personal loans or borrowed money',
    icon: '📄',
    successRate: 68,
    examples: ['Unpaid loans', 'Repayment terms', 'Interest disputes'],
  },
  {
    id: 'neighbor_dispute',
    title: 'Neighbor Dispute',
    description: 'Conflicts with neighbors or shared property',
    icon: '🏘️',
    successRate: 64,
    examples: ['Noise complaints', 'Property boundaries', 'Shared costs'],
  },
];

const ClassifyPage = () => {
  const navigate = useNavigate();
  const { dispute, updateDispute } = useDispute();
  const [selectedType, setSelectedType] = useState(null);
  const [amountClaimed, setAmountClaimed] = useState('');
  const [showAmountInput, setShowAmountInput] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!dispute || !dispute.id) {
      navigate('/start');
    }
  }, [dispute, navigate]);

  const handleSelectType = (type) => {
    setSelectedType(type.id);
    setShowAmountInput(true);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const amount = parseFloat(amountClaimed);
    if (!amount || amount <= 0 || amount > 15000) {
      setError('Amount must be between €0.01 and €15,000');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const data = await classifyDispute({
        disputeId: dispute.id,
        type: selectedType,
        amountClaimed: amount,
      });

      updateDispute(data);
      navigate('/assessment');
    } catch (err) {
      setError(err.message || 'Failed to classify dispute. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!dispute) {
    return null;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <ProgressBar current={1} total={5} phase="Classification" />

      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold mb-3">What Type of Dispute Do You Have?</h1>
        <p className="text-gray-600">
          Select the category that best describes your situation. Our AI will use this to match similar cases.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {!showAmountInput ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {DISPUTE_TYPES.map((type) => (
            <button
              key={type.id}
              onClick={() => handleSelectType(type)}
              disabled={submitting}
              className={`card text-left transition-all hover:shadow-lg hover:scale-[1.02] ${
                selectedType === type.id
                  ? 'ring-2 ring-primary-500 bg-primary-50'
                  : 'hover:border-primary-300'
              } ${submitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
            <div className="flex items-start justify-between mb-3">
              <div className="text-4xl">{type.icon}</div>
              <div className="text-right">
                <div className="text-xs text-gray-500 mb-1">Success Rate</div>
                <div className="text-lg font-bold text-green-600">{type.successRate}%</div>
              </div>
            </div>

            <h3 className="text-xl font-bold mb-2">{type.title}</h3>
            <p className="text-sm text-gray-600 mb-3">{type.description}</p>

            <div className="pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-2">Common examples:</p>
              <ul className="text-xs text-gray-600 space-y-1">
                {type.examples.map((example, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="mr-1">•</span>
                    <span>{example}</span>
                  </li>
                ))}
              </ul>
            </div>

            {selectedType === type.id && submitting && (
              <div className="mt-4 text-center">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
              </div>
            )}
          </button>
        ))}
      </div>
      ) : (
        <div className="max-w-2xl mx-auto">
          <div className="card mb-6">
            <h2 className="text-2xl font-bold mb-4">What is the Amount in Dispute?</h2>
            <p className="text-gray-600 mb-6">
              Enter the amount you're claiming (up to €15,000). This helps us find relevant similar cases.
            </p>

            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-2">
                  Amount Claimed (€)
                </label>
                <input
                  id="amount"
                  type="number"
                  min="0.01"
                  max="15000"
                  step="0.01"
                  className="input"
                  placeholder="e.g., 2500"
                  value={amountClaimed}
                  onChange={(e) => setAmountClaimed(e.target.value)}
                  required
                  disabled={submitting}
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-2">
                  Maximum €15,000 for AML compliance
                </p>
              </div>

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => {
                    setShowAmountInput(false);
                    setSelectedType(null);
                    setAmountClaimed('');
                  }}
                  className="btn-secondary"
                  disabled={submitting}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Continue to Assessment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card bg-blue-50 border-blue-200">
        <div className="flex items-start">
          <div className="text-2xl mr-3">ℹ️</div>
          <div>
            <h4 className="font-bold mb-2">Why does this matter?</h4>
            <p className="text-sm text-gray-700">
              Our AI analyzes thousands of similar cases to predict likely outcomes and fair settlement ranges.
              The success rate shows how often disputes like yours reach agreement through our platform.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassifyPage;
